import { createContext, useContext, useRef, FC, ReactNode, useMemo, useEffect } from 'react';

import { useProvider } from 'mobx/hooks/useProvidersOptions';
import { useStores } from 'mobx/hooks/useStores';

import { defaultNonSymptomUrgencyValue } from 'utils/urgencyMappings';

import Patient from 'models/Patient';
import PatientLocation from 'models/PatientLocation';
import PatientProvider from 'models/PatientProvider';

import { ISelectOption } from 'components/UIkit/atoms/Dropdown/Select.shared';

export type TicketTypeDefaultFields = {
  locationId: ISelectOption<PatientLocation> | null;
  providerId: ISelectOption<PatientProvider> | null;
  assignee: ISelectOption<number> | null;
  urgency: { label: string; value: number };
};

type TicketCreationContextType = {
  updateTicketTypeDefaultField: <K extends keyof TicketTypeDefaultFields>(
    field: K,
    value: TicketTypeDefaultFields[K]
  ) => void;
  getTicketTypeDefaultFields: () => TicketTypeDefaultFields;
  useInitTicketTypeDefaultFields: (patient: Patient) => void;
};

const TicketCreationContext = createContext<TicketCreationContextType | null>(null);

export const TicketTypeDefaultFieldsProvider: FC<{ children: ReactNode }> = ({ children }) => {
  // hold the current default fields
  // This prevents re-renders when values change, since form items
  // only read defaults in event handlers, not during render
  const ref = useRef<TicketTypeDefaultFields>({
    locationId: null,
    providerId: null,
    assignee: null,
    urgency: defaultNonSymptomUrgencyValue
  });

  const value = useMemo<TicketCreationContextType>(() => {
    return {
      getTicketTypeDefaultFields: () => ({ ...ref.current }),
      updateTicketTypeDefaultField: (field, value) => {
        ref.current[field] = value;
      },
      useInitTicketTypeDefaultFields: (patient: Patient) => {
        const { locationsStore } = useStores();
        const patientProvider = useProvider(patient?.providerId);

        // Initialize context with patient defaults on mount
        useEffect(
          function initializeDefaultValues() {
            const locationId =
              patient?.location && patient.location.visible
                ? {
                    label: locationsStore.getLocationById(patient?.location.id)?.name,
                    value: patient.location
                  }
                : null;

            Object.assign(ref.current, {
              locationId,
              providerId: patientProvider?.value ? patientProvider : null,
              assignee: null,
              urgency: defaultNonSymptomUrgencyValue
            });
          },
          // eslint-disable-next-line react-hooks/exhaustive-deps
          [patient.id, patientProvider?.value]
        );
      }
    };
  }, [ref]);

  return <TicketCreationContext.Provider value={value}>{children}</TicketCreationContext.Provider>;
};

export const useTicketTypeDefaultFields = () => {
  const context = useContext(TicketCreationContext);
  if (!context) {
    throw new Error(
      'useTicketTypeDefaultFields must be used within TicketTypeDefaultFieldsProvider'
    );
  }
  return context;
};
